var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { boundMethod } from 'autobind-decorator';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { PureComponent } from 'react';
import { ajaxSend } from 'api/api';
import { pubSub } from 'utils/pubSub';
import { presenceGetAll, presenceGetTotal, } from './api/presence';
const DEFAULT_FETCH_THROTTLE = 300;
const FILTER_MAP = {
    online: {
        status: 'online',
    },
    offline: {
        status: 'offline',
    },
    away: {
        status: 'away',
    },
};
export const withPresence = (Component, settings = {}) => {
    class WithPresence extends PureComponent {
        constructor(props) {
            super(props);
            this.timeout = null;
            this.state = {
                items: [],
                total: 0,
                isAssignedOnly: false,
                searchTerm: '',
                filter: 'all',
                nextPagePath: null,
                previousPagePath: null,
                itemsAsyncStatus: null,
                totalAsyncStatus: null,
                favourites: [],
            };
            this.memoizedGetInjectedProps = memoize(this.memoizedGetInjectedProps, isEqual).bind(this); // We have to bind it like this. Using @boundMethod won't work
        }
        componentDidMount() {
            this.agentStatusChangedSubscription = pubSub.subscribe('agentStatusChanged', this.onPresenceAgentStatusChanged);
        }
        componentWillUnmount() {
            var _a;
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            (_a = this.agentStatusChangedSubscription) === null || _a === void 0 ? void 0 : _a.call(this);
        }
        // Handle socket connection updates.
        onPresenceAgentStatusChanged(payload) {
            var _a, _b, _c;
            const userId = (_a = payload === null || payload === void 0 ? void 0 : payload.params) === null || _a === void 0 ? void 0 : _a.user_id;
            const loadDescriptions = (_c = (_b = payload === null || payload === void 0 ? void 0 : payload.params) === null || _b === void 0 ? void 0 : _b.presence) === null || _c === void 0 ? void 0 : _c.load_descriptions;
            // payload doesn't include workloads_by_channel, so infer it from load_descriptions
            const workloads_by_channel = loadDescriptions === null || loadDescriptions === void 0 ? void 0 : loadDescriptions.reduce((acc, d) => (Object.assign(Object.assign({}, acc), { [d.conversation_channel]: [
                    ...(acc[d.conversation_channel] || []),
                    d.csid,
                ] })), {});
            this.setState(({ items: prevItems }) => {
                const index = userId
                    ? prevItems.findIndex((x) => x.agent_id === userId)
                    : -1;
                if (index > -1) {
                    const items = [...prevItems];
                    const item = Object.assign(Object.assign({}, prevItems[index]), { status: payload.params.new_status, connection_status: payload.params.new_status, simultaneous_ring: payload.params.presence.simultaneous_ringing, non_working_channels: payload.params.working_channels
                            .filter((x) => !x.working)
                            .map((x) => x.conversation_channel), workloads_by_channel });
                    items[index] = item;
                    return {
                        items,
                    };
                }
                return null;
            });
        }
        queryItemsWithTotal(favourites) {
            return new Promise((resolve) => {
                const runResolver = () => resolve(Promise.all([this.getItems(), this.getTotal()]));
                if (favourites) {
                    this.setState({ favourites }, runResolver);
                }
                else {
                    runResolver();
                }
            });
        }
        applyFilter() {
            const { filter, favourites, isAssignedOnly } = this.state;
            const queryFilter = Object.assign({}, (FILTER_MAP[filter] || {}));
            if (favourites.length > 0) {
                queryFilter.favourites = favourites.filter(Boolean).join(',');
            }
            if (isAssignedOnly) {
                queryFilter.has_assigned_conversations = true;
            }
            if (filter === 'favourite') {
                queryFilter.limit = favourites.length;
            }
            return queryFilter;
        }
        getItems(path) {
            this.setState({ itemsAsyncStatus: 'loading' });
            const req = path
                ? ajaxSend({ path })
                : presenceGetAll(Object.assign({ search: this.state.searchTerm, limit: settings.pageSize }, this.applyFilter()));
            return req.then(this.handleQueryResolve).catch(this.handleQueryReject);
        }
        provideFavourites(favourites) {
            this.setState({ favourites });
        }
        provideAssignedOnly(isAssignedOnly) {
            this.setState({ isAssignedOnly });
        }
        provideFilter(filter) {
            this.setState({ filter });
        }
        provideSearchTerm(searchTerm) {
            this.setState({ searchTerm });
        }
        getTotal() {
            // If filter is `favourite` ignore /total and use limit value, since pagination is ignored in this case.
            if (this.state.filter === 'favourite') {
                const { limit } = this.applyFilter();
                const total = typeof limit === 'number' ? limit : 0;
                this.setState({ total });
                return Promise.resolve({
                    data: {
                        total,
                    },
                });
            }
            this.setState({ totalAsyncStatus: 'loading' });
            return presenceGetTotal(this.applyFilter())
                .then((res) => {
                this.setState({
                    totalAsyncStatus: 'succeeded',
                    total: res.data.total,
                });
                return res;
            })
                .catch((err) => {
                this.setState({ totalAsyncStatus: 'failed' });
                return err;
            });
        }
        nextPage() {
            const { nextPagePath } = this.state;
            if (nextPagePath) {
                return this.getItems(nextPagePath);
            }
            throw new Error('Next page does not exist');
        }
        previousPage() {
            const { previousPagePath } = this.state;
            if (previousPagePath) {
                return this.getItems(previousPagePath);
            }
            throw new Error('Previous page does not exist');
        }
        handleSearchTermChange(searchTerm = '', fetchThrottle = DEFAULT_FETCH_THROTTLE) {
            return new Promise((resolve) => {
                this.setState({ searchTerm }, () => {
                    if (fetchThrottle >= 0) {
                        if (this.timeout) {
                            clearTimeout(this.timeout);
                        }
                        this.timeout = setTimeout(() => {
                            resolve(this.queryItemsWithTotal());
                        }, fetchThrottle);
                    }
                    else {
                        resolve();
                    }
                });
            });
        }
        handleAssignedFilterChange() {
            return new Promise((resolve) => {
                this.setState({ isAssignedOnly: !this.state.isAssignedOnly }, () => {
                    resolve(this.queryItemsWithTotal());
                });
            });
        }
        handleFilterChange(filter) {
            return new Promise((resolve) => {
                this.setState({ filter }, () => {
                    resolve(this.queryItemsWithTotal());
                });
            });
        }
        handleQueryResolve(response) {
            this.setState(({ filter, total, totalAsyncStatus }) => {
                var _a, _b;
                // If limit is 0 there're no items to fetch/show next.
                // favourite filter also replaces limit, hence hide next/prev pages.
                const isFavourite = filter === 'favourite';
                const nextPagePath = ((_a = response.meta.next_page) === null || _a === void 0 ? void 0 : _a.includes('limit=0')) || isFavourite
                    ? null
                    : response.meta.next_page;
                const previousPagePath = ((_b = response.meta.prev_page) === null || _b === void 0 ? void 0 : _b.includes('limit=0')) || isFavourite
                    ? null
                    : response.meta.prev_page;
                const items = this.filterFavourite(response.data.presence);
                const totalState = isFavourite
                    ? {
                        total: items.length,
                        totalAsyncStatus: 'succeeded',
                    }
                    : {
                        total,
                        totalAsyncStatus,
                    };
                return Object.assign({ itemsAsyncStatus: 'succeeded', items,
                    nextPagePath,
                    previousPagePath }, totalState);
            });
            return response;
        }
        filterFavourite(items) {
            if (this.state.filter !== 'favourite' ||
                (!this.state.isAssignedOnly && !this.state.searchTerm)) {
                return items;
            }
            // Backend does not filter out favourites but rather sorts it out to top applying limit.
            // Hence filter out here if search or isAssignedOnly is provided.
            return items.filter((x) => this.state.favourites.includes(x.agent_id));
        }
        handleQueryReject(error) {
            this.setState({
                itemsAsyncStatus: 'failed',
            });
            return error;
        }
        getInjectedProps(props, state) {
            const { items, total, isAssignedOnly, searchTerm, filter, nextPagePath, previousPagePath, itemsAsyncStatus, totalAsyncStatus, } = state;
            return this.memoizedGetInjectedProps(searchTerm, items, total, isAssignedOnly, filter, itemsAsyncStatus, totalAsyncStatus, nextPagePath, previousPagePath);
        }
        memoizedGetInjectedProps(searchTerm, items, total, isAssignedOnly, filter, itemsAsyncStatus, totalAsyncStatus, nextPagePath, previousPagePath) {
            return {
                searchTerm,
                items,
                total,
                filter,
                isAssignedOnly,
                itemsAsyncStatus,
                totalAsyncStatus,
                hasNextPage: !!nextPagePath,
                hasPreviousPage: !!previousPagePath,
                getItems: this.getItems,
                getTotal: this.getTotal,
                searchTermChange: this.handleSearchTermChange,
                filterChange: this.handleFilterChange,
                nextPage: this.nextPage,
                previousPage: this.previousPage,
                queryItemsWithTotal: this.queryItemsWithTotal,
                assignedFilterChange: this.handleAssignedFilterChange,
                provideFavourites: this.provideFavourites,
                provideFilter: this.provideFilter,
                provideAssignedOnly: this.provideAssignedOnly,
                provideSearchTerm: this.provideSearchTerm,
            };
        }
        render() {
            return (_jsx(Component, Object.assign({}, this.props, { presence: this.getInjectedProps(this.props, this.state) })));
        }
    }
    __decorate([
        boundMethod
    ], WithPresence.prototype, "onPresenceAgentStatusChanged", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "queryItemsWithTotal", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "applyFilter", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "getItems", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "provideFavourites", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "provideAssignedOnly", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "provideFilter", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "provideSearchTerm", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "getTotal", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "nextPage", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "previousPage", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "handleSearchTermChange", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "handleAssignedFilterChange", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "handleFilterChange", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "handleQueryResolve", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "filterFavourite", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "handleQueryReject", null);
    __decorate([
        boundMethod
    ], WithPresence.prototype, "getInjectedProps", null);
    return WithPresence;
};
